/**
 * Catalog des tests techniques + sélection courante.
 *
 * @remarks
 * Découpé du legacy `useTechTestsStore` (929 LOC, monolithique). Ne contient
 * que ce qui touche au catalog et à la progression des évaluations. Le
 * workspace, les notes, les working docs, le pomodoro, etc. vivent dans des
 * stores dédiés (`use-evaluation-workspace-store`, etc.).
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { calculateTestStatus } from '../lib/status-helpers';
import type { EvaluationSubmission, TechTest, TestEvaluation } from '../types';
import { useEvaluationWorkspaceStore } from './use-evaluation-workspace-store';
import { useWorkingDocsStore } from './use-working-docs-store';

interface TechTestsState {
  tests: TechTest[];
  selectedTestId: string | null;
  selectedEvaluationId: string | null;

  // Computed getters (fonctions, comme legacy — non-persistés)
  selectedTest: () => TechTest | null;
  selectedEvaluation: () => TestEvaluation | null;

  // Actions
  setSelectedTest: (testId: string | null) => void;
  setSelectedEvaluation: (evaluationId: string | null) => void;
  canStartEvaluation: (testId: string, evaluationId: string) => boolean;
  startEvaluation: (testId: string, evaluationId: string, attemptKey?: string) => void;
  completeEvaluation: (testId: string, evaluationId: string) => void;
  submitEvaluationWork: (
    testId: string,
    evaluationId: string,
    submission: EvaluationSubmission,
  ) => void;
  abandonEvaluation: (testId: string, evaluationId: string) => void;
  upsertTest: (test: TechTest) => void;
  resetTests: () => void;
}

const updateTestStatus = (test: TechTest): TechTest => ({
  ...test,
  status: calculateTestStatus(test),
});

/**
 * Reporte la `submission` déjà connue quand le test entrant n'en apporte pas.
 *
 * @remarks
 * La projection d'attempts du détail back (ms-graph `findByTestKey`) n'inclut
 * PAS `submittedResources` : le refetch post-submit revient donc sans
 * `submission` et écraserait le badge posé à la soumission (optimiste, ou
 * rechargé depuis le store persisté). On préserve la submission existante quand
 * l'entrant n'en a pas — jamais l'inverse : `abandonEvaluation` la retire
 * explicitement, donc aucune resurrection après abandon.
 */
const preserveSubmissions = (existing: TechTest, incoming: TechTest): TechTest => ({
  ...incoming,
  evaluations: incoming.evaluations.map((ev) => {
    if (ev.submission) return ev;
    const prev = existing.evaluations.find((e) => e.id === ev.id);
    return prev?.submission ? { ...ev, submission: prev.submission } : ev;
  }),
});

const DATA_VERSION = 1; // v1 saas-front-v2 (rebase complet, indépendant du legacy v16)

export const useTechTestsStore = create<TechTestsState>()(
  devtools(
    persist(
      (set, get) => ({
        tests: [],
        selectedTestId: null,
        selectedEvaluationId: null,

        selectedTest: () => {
          const { tests, selectedTestId } = get();
          if (!selectedTestId) return null;
          return tests.find((t) => t.id === selectedTestId) ?? null;
        },

        selectedEvaluation: () => {
          const { selectedEvaluationId } = get();
          const test = get().selectedTest();
          if (!test || !selectedEvaluationId) return null;
          return test.evaluations.find((ev) => ev.id === selectedEvaluationId) ?? null;
        },

        setSelectedTest: (testId) => {
          set({ selectedTestId: testId, selectedEvaluationId: null });
          // Cross-store reset : changer de test invalide la sélection workspace
          // (document, working doc, scroll, heading…). Centralisé ici plutôt
          // que de demander aux callers de le faire — règle métier.
          useEvaluationWorkspaceStore.getState().setSelectedDocument(null);
          useWorkingDocsStore.getState().setActiveWorkingDoc(null);
        },

        setSelectedEvaluation: (evaluationId) => {
          set({ selectedEvaluationId: evaluationId });
          useEvaluationWorkspaceStore.getState().setSelectedDocument(null);
          useWorkingDocsStore.getState().setActiveWorkingDoc(null);
        },

        canStartEvaluation: (testId, evaluationId) => {
          const test = get().tests.find((t) => t.id === testId);
          if (!test) return false;
          const evaluation = test.evaluations.find((ev) => ev.id === evaluationId);
          if (!evaluation) return false;

          if (evaluation.status === 'in-progress' || evaluation.status === 'completed')
            return false;

          const now = Date.now();
          if (test.scheduledStartAt && now < test.scheduledStartAt) return false;

          if (evaluation.scheduledStartAt) {
            return now >= evaluation.scheduledStartAt;
          }

          if (evaluation.dependencies.length === 0) return true;
          return evaluation.dependencies.every((depId) => {
            const dep = test.evaluations.find((ev) => ev.id === depId);
            return dep?.status === 'completed';
          });
        },

        startEvaluation: (testId, evaluationId, attemptKey) => {
          set((state) => ({
            tests: state.tests.map((test) => {
              if (test.id !== testId) return test;
              const evaluations = test.evaluations.map((ev) => {
                if (ev.id !== evaluationId) return ev;
                if (!state.canStartEvaluation(testId, evaluationId)) return ev;
                return {
                  ...ev,
                  status: 'in-progress' as const,
                  startedAt: Date.now(),
                  ...(attemptKey ? { activeAttemptKey: attemptKey } : {}),
                };
              });
              return updateTestStatus({ ...test, evaluations });
            }),
          }));
        },

        completeEvaluation: (testId, evaluationId) => {
          set((state) => ({
            tests: state.tests.map((test) => {
              if (test.id !== testId) return test;
              const evaluations = test.evaluations.map((ev) => {
                if (ev.id !== evaluationId) return ev;
                if (ev.status !== 'in-progress') return ev;
                return { ...ev, status: 'completed' as const, completedAt: Date.now() };
              });
              const unlocked = evaluations.map((ev) => {
                if (
                  ev.dependencies.includes(evaluationId) &&
                  ev.status === 'locked' &&
                  !ev.scheduledStartAt
                ) {
                  const allDepsCompleted = ev.dependencies.every((depId) => {
                    const dep = evaluations.find((e) => e.id === depId);
                    return dep?.status === 'completed';
                  });
                  if (allDepsCompleted) return { ...ev, status: 'available' as const };
                }
                return ev;
              });
              return updateTestStatus({ ...test, evaluations: unlocked });
            }),
          }));
        },

        submitEvaluationWork: (testId, evaluationId, submission) => {
          set((state) => ({
            tests: state.tests.map((test) => {
              if (test.id !== testId) return test;
              const evaluations = test.evaluations.map((ev) => {
                if (ev.id !== evaluationId) return ev;
                return { ...ev, submission };
              });
              return updateTestStatus({ ...test, evaluations });
            }),
          }));
        },

        abandonEvaluation: (testId, evaluationId) => {
          set((state) => ({
            tests: state.tests.map((test) => {
              if (test.id !== testId) return test;
              const evaluations = test.evaluations.map((ev) => {
                if (ev.id !== evaluationId) return ev;
                if (ev.status !== 'in-progress') return ev;
                const { submission: _submission, ...rest } = ev;
                void _submission;
                return { ...rest, status: 'failed' as const };
              });
              return updateTestStatus({ ...test, evaluations });
            }),
          }));
        },

        upsertTest: (test) => {
          set((state) => {
            const existing = state.tests.find((t) => t.id === test.id);
            if (!existing) return { tests: [...state.tests, test] };
            const merged = preserveSubmissions(existing, test);
            return { tests: state.tests.map((t) => (t.id === test.id ? merged : t)) };
          });
        },

        resetTests: () => {
          set({ tests: [], selectedTestId: null, selectedEvaluationId: null });
        },
      }),
      {
        name: 'saas-front-v2:tech-tests',
        version: DATA_VERSION,
        partialize: (state) => ({
          tests: state.tests,
          selectedTestId: state.selectedTestId,
          selectedEvaluationId: state.selectedEvaluationId,
        }),
        // Aucune version antérieure n'existe : `migrate` n'est atteint que sur un
        // payload d'origine inconnue — on repart sans test en cache (le prochain
        // fetch API les repeuple).
        // ⚠️ Au prochain bump, adapter AVANT ce retour en comparant avec `<` :
        // `if (oldVersion < 2) { …adapter… }`. Ne jamais tester
        // `oldVersion === DATA_VERSION` : zustand n'appelle `migrate` que si les
        // versions diffèrent, la branche serait morte. Cascade de référence :
        // `use-layout-store`. (SAAS-903)
        migrate: () => ({
          tests: [],
          selectedTestId: null,
          selectedEvaluationId: null,
        }),
      },
    ),
    { name: 'tech-tests', enabled: import.meta.env.DEV },
  ),
);
